(function () {
  'use strict';

  const viewportCategory = () => {
    if (window.matchMedia('(max-width: 699px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 1023px)').matches) return 'tablet';
    return 'desktop';
  };

  const track = (eventName, details) => {
    if (!eventName) return;

    const payload = Object.assign(
      {
        event: eventName,
        viewport_category: viewportCategory()
      },
      details || {}
    );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('new-main-ob:analytics', { detail: payload }));
  };

  const bindAnalytics = () => {
    if (document.documentElement.dataset.nmobAnalyticsBound === 'true') return;
    document.documentElement.dataset.nmobAnalyticsBound = 'true';

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('[data-nmob-event]');
      if (!link) return;

      track(link.dataset.nmobEvent, {
        cta_location: link.dataset.nmobLocation || '',
        product_handle: link.dataset.nmobProduct || '',
        problem_collection: link.dataset.nmobProblem || '',
        destination: link.href || ''
      });
    });
  };

  const bindMobileChat = (root) => {
    if (root.dataset.nmobChatBound === 'true') return;

    const trigger = document.querySelector('#new-main-ob-compare');
    if (!trigger) return;

    const mobileChatQuery = window.matchMedia('(max-width: 999px)');
    root.dataset.nmobChatBound = 'true';

    let revealed = document.body.classList.contains('clearview-chat-ready');

    const reveal = () => {
      if (revealed || !mobileChatQuery.matches) return;

      revealed = true;
      document.body.classList.add('clearview-chat-ready');
      track('new_main_ob_chat_launcher_impression', {
        cta_location: 'system_comparison',
        destination: window.location.href
      });
      window.removeEventListener('scroll', checkPosition);
    };

    const checkPosition = () => {
      if (!mobileChatQuery.matches || revealed) return;
      if (window.scrollY < 72) return;

      const rect = trigger.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) reveal();
      else if (rect.bottom <= 0) reveal();
    };

    window.addEventListener('scroll', checkPosition, { passive: true });

    const onMediaChange = () => {
      if (mobileChatQuery.matches) checkPosition();
    };

    mobileChatQuery.addEventListener('change', onMediaChange);
    checkPosition();

    root.nmobChatCleanup = () => {
      window.removeEventListener('scroll', checkPosition);
      mobileChatQuery.removeEventListener('change', onMediaChange);
      document.body.classList.remove('clearview-chat-ready');
      delete root.dataset.nmobChatBound;
    };
  };

  const bindStickyCta = (root) => {
    const sticky = root.querySelector('[data-nmob-sticky]');
    const actions = root.querySelector('[data-nmob-hero-actions]');
    if (!sticky || !actions || sticky.dataset.nmobBound === 'true') return;

    sticky.dataset.nmobBound = 'true';

    const setActive = (active) => {
      sticky.classList.toggle('is-active', active);
      sticky.setAttribute('aria-hidden', active ? 'false' : 'true');
      sticky.tabIndex = active ? 0 : -1;
    };

    setActive(false);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => setActive(!entries[0].isIntersecting),
        { threshold: 0.05 }
      );
      observer.observe(actions);
    } else {
      const update = () => setActive(actions.getBoundingClientRect().bottom < 0);
      update();
      window.addEventListener('scroll', update, { passive: true });
    }
  };

  const bindCatalogue = (root) => {
    root.querySelectorAll('[data-nmob-catalogue]').forEach((catalogue) => {
      if (catalogue.dataset.nmobBound === 'true') return;
      catalogue.dataset.nmobBound = 'true';

      catalogue.addEventListener('toggle', () => {
        if (!catalogue.open || catalogue.dataset.nmobTracked === 'true') return;
        catalogue.dataset.nmobTracked = 'true';
        track('new_main_ob_full_catalogue_view', {
          cta_location: 'catalogue_disclosure',
          destination: window.location.href
        });
      });
    });
  };

  const bindReadMore = (scope) => {
    scope.querySelectorAll('[data-nmob-read-more]').forEach((disclosure) => {
      const content = disclosure.querySelector('[data-nmob-read-more-content]');
      const toggle = disclosure.querySelector('[data-nmob-read-more-toggle]');
      if (!content || !toggle || disclosure.dataset.nmobBound === 'true') return;

      disclosure.dataset.nmobBound = 'true';

      const updateOverflow = () => {
        if (disclosure.classList.contains('is-expanded')) return;
        const hasOverflow = content.scrollHeight > content.clientHeight + 2;
        disclosure.classList.toggle('has-overflow', hasOverflow);
        toggle.hidden = !hasOverflow;
      };

      toggle.addEventListener('click', () => {
        const isExpanded = disclosure.classList.contains('is-expanded');

        if (isExpanded) {
          const toggleTop = toggle.getBoundingClientRect().top;
          disclosure.classList.remove('is-expanded');
          toggle.setAttribute('aria-expanded', 'false');
          toggle.textContent = toggle.dataset.moreLabel || 'Read full buying guide';

          requestAnimationFrame(() => {
            const newToggleTop = toggle.getBoundingClientRect().top;
            window.scrollBy({ top: newToggleTop - toggleTop, behavior: 'auto' });
            updateOverflow();
          });
        } else {
          disclosure.classList.add('is-expanded');
          toggle.setAttribute('aria-expanded', 'true');
          toggle.textContent = toggle.dataset.lessLabel || 'Show less';
        }
      });

      updateOverflow();
      window.addEventListener('resize', updateOverflow, { passive: true });
    });
  };

  const init = (scope) => {
    bindReadMore(scope);

    const root = scope.matches && scope.matches('[data-nmob-page]')
      ? scope
      : scope.querySelector('[data-nmob-page]');

    if (!root) return;

    bindAnalytics();
    bindMobileChat(root);
    bindStickyCta(root);
    bindCatalogue(document);

    if (document.documentElement.dataset.nmobViewed !== 'true') {
      document.documentElement.dataset.nmobViewed = 'true';
      track('new_main_ob_view', {
        cta_location: 'page',
        destination: window.location.href
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches('[data-nmob-page]')
      ? event.target
      : event.target.querySelector('[data-nmob-page]');
    if (root && typeof root.nmobChatCleanup === 'function') root.nmobChatCleanup();
  });
})();
